/**
 * CSV export of the admin usage report: one row per person and model, for both
 * the engineer tools (requests only — AWS records no tokens per person there)
 * and the portal (requests and tokens).
 */

export interface ToolUsageCsvRow {
  person: string;
  model: string | null;
  requests: number;
  refused: number;
  last_used: string | null;
}

export interface PortalUsageCsvRow {
  actor: string;
  model_id: string;
  requests: number;
  failed: number;
  incomplete: number;
  input_tokens: number;
  output_tokens: number;
  cache_read_tokens: number;
  cache_write_tokens: number;
  last_used: string | null;
}

export interface UsageCsvInput {
  start: string;
  end: string;
  tools: ToolUsageCsvRow[];
  portal: PortalUsageCsvRow[];
  cost_by_person?: Record<string, number>;
  cost_by_vendor?: Record<string, Record<string, number>>;
  portal_cost?: number | null;
  other_cost?: number | null;
}

const HEADER = [
  'source',
  'person',
  'model',
  'requests',
  'refused_or_failed',
  'incomplete',
  'input_tokens',
  'output_tokens',
  'cache_read_tokens',
  'cache_write_tokens',
  'last_used',
  'cost_usd',
  'period_start',
  'period_end',
];

function cell(value: string | number | null): string {
  const text = value === null ? '' : String(value);
  return /[",\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

const VENDOR_LABEL: Record<string, string> = {
  claude: 'Claude (Desktop, Cowork, Claude Code)',
  gpt: 'GPT (Codex)',
};

function billRow(
  person: string,
  cost: number,
  report: UsageCsvInput,
  label: string | null = null,
): (string | number | null)[] {
  return ['AWS bill', person, label, null, null, null, null, null, null, null, null, cost.toFixed(2), report.start, report.end];
}

export function usageCsv(report: UsageCsvInput): string {
  const rows: (string | number | null)[][] = [
    ...report.tools.map((r) => [
      'Desktop, Cowork, Claude Code, Codex',
      r.person,
      r.model,
      r.requests,
      r.refused,
      null,
      null,
      null,
      null,
      null,
      r.last_used,
      null,
      report.start,
      report.end,
    ]),
    ...report.portal.map((r) => [
      'Portal',
      r.actor,
      r.model_id,
      r.requests,
      r.failed,
      r.incomplete,
      r.input_tokens,
      r.output_tokens,
      r.cache_read_tokens,
      r.cache_write_tokens,
      r.last_used,
      null,
      report.start,
      report.end,
    ]),
    // The bill knows the vendor, not the app: Claude is Desktop/Cowork/Claude Code, GPT is Codex.
    ...Object.entries(report.cost_by_vendor ?? {}).flatMap(([person, vendors]) =>
      Object.entries(vendors).map(([vendor, cost]) =>
        billRow(person, cost, report, VENDOR_LABEL[vendor] ?? vendor),
      ),
    ),
    ...(report.portal_cost != null ? [billRow('Portal (all users)', report.portal_cost, report)] : []),
    ...(report.other_cost != null ? [billRow('Other', report.other_cost, report)] : []),
  ];
  return [HEADER, ...rows].map((row) => row.map(cell).join(',')).join('\r\n') + '\r\n';
}

export function downloadUsageCsv(report: UsageCsvInput): void {
  const blob = new Blob([usageCsv(report)], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `trident-ai-usage_${report.start}_to_${report.end}.csv`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}
